import { z } from 'zod';
import { TicketingApiError } from './client.js';
import { DEFERRED_PAYMENT_METHOD_CODE, parseCustomerPaymentMode, toPaymentMethod, } from './order-payments.js';
// TODO: the billetterie now serves /admin/cse/{cseId}/orders (seen 2026-11-03); the subvention
// scan can drop its cards/customer-set workaround for it.
// See packages/ticketing-api/subvention.CLAUDE.md.
const orderItemSchema = z.looseObject({
    '@id': z.string(),
    id: z.number(),
    productName: z.string().nullable().optional(),
    productSlug: z.string().nullable().optional(),
    variant: z.string().nullable().optional(),
    quantity: z.number(),
    unitPrice: z.number(),
    total: z.number(),
});
const orderSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('Order'),
    id: z.number(),
    tokenValue: z.string(),
    number: z.string().nullable().optional(),
    customer: z.string().nullable().optional(),
    state: z.string(),
    paymentState: z.string(),
    checkoutCompletedAt: z.string().nullable().optional(),
    itemsTotal: z.number(),
    total: z.number(),
    items: z.array(orderItemSchema),
    payments: z.array(z.looseObject({ method: z.string().nullable().optional() })).optional(),
    notes: z.string().nullable().optional(),
});
const orderAdjustmentSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('Adjustment'),
    id: z.number(),
    type: z.string(),
    label: z.string().nullable().optional(),
    amount: z.number(),
    neutral: z.boolean(),
    order: z.string().nullable(),
    orderItem: z.string().nullable(),
});
const productSchema = z.looseObject({
    '@id': z.string(),
    code: z.string(),
    mainTaxon: z.string().nullable().optional(),
});
const CUSTOMER_CHUNK_SIZE = 300; // Cloudflare answers 431 past ~10 KB of URL (~400 ids)
// A power of two, so a failing page splits into two exact pages down to a single order.
const ORDERS_PER_PAGE = 1024;
const ORDER_CHUNK_CONCURRENCY = 4;
const DEFAULT_CONCURRENCY = 10;
const extractIdFromIri = (iri) => {
    const last = iri.split('/').pop();
    return last ? Number(last) : Number.NaN;
};
const chunkArray = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size)
        out.push(arr.slice(i, i + size));
    return out;
};
const mapWithConcurrency = async (items, concurrency, fn) => {
    const results = new Array(items.length);
    let cursor = 0;
    const worker = async () => {
        while (true) {
            const i = cursor++;
            if (i >= items.length)
                return;
            results[i] = await fn(items[i], i);
        }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
    return results;
};
/**
 * Fetch every customer ID associated with a CSE via its cards.
 * Cards embed `user.customer.@id` inline so this is a single paginated scan.
 */
const getCseCustomerIds = async (client, cseId) => {
    const cards = await client.getCseCards(cseId);
    const ids = new Set();
    for (const card of cards) {
        const customerIri = card.user?.customer?.['@id'];
        if (!customerIri)
            continue;
        const id = extractIdFromIri(customerIri);
        if (!Number.isNaN(id))
            ids.add(id);
    }
    return [...ids];
};
// No `order[...]` sort: callers aggregate or sort client-side, and a sort only reshuffles which
// pages hit an order the billetterie cannot serialize (see getOrdersForCustomer).
const buildOrdersQuery = ({ customerIds, startDate, endDate }, page, itemsPerPage) => {
    const params = new URLSearchParams();
    for (const id of customerIds)
        params.append('customer.id[]', String(id));
    params.set('checkoutCompletedAt[after]', startDate);
    params.set('checkoutCompletedAt[before]', endDate);
    params.set('itemsPerPage', String(itemsPerPage));
    params.set('page', String(page));
    return params.toString();
};
const fetchOrdersPage = async (client, filter, page, itemsPerPage) => {
    const query = buildOrdersQuery(filter, page, itemsPerPage);
    const path = filter.cseId === null ? '/admin/orders' : `/admin/cse/${filter.cseId}/orders`;
    const response = await client.get(`${path}?${query}`);
    return {
        orders: z.array(orderSchema).parse(response['hydra:member'] ?? []),
        total: response['hydra:totalItems'] ?? 0,
    };
};
// An out-of-range page returns empty members (no IRI generation), so this stays a 200 even when
// every real page holds an order the billetterie cannot serialize.
const countOrders = async (client, filter) => (await fetchOrdersPage(client, filter, 1000000, 1)).total;
const combineScans = (scans) => ({
    orders: scans.flatMap((scan) => scan.orders),
    unreadableOrders: scans.reduce((sum, scan) => sum + scan.unreadableOrders, 0),
});
const isUnserializableOrderError = (err) => err instanceof TicketingApiError &&
    err.status === 400 &&
    (err.detail?.includes('Unable to generate an IRI') ?? false);
// Caps the requests one scan keeps in flight, however its halvings fan out.
const createLimiter = (concurrency) => {
    let active = 0;
    const queue = [];
    return async (fn) => {
        if (active >= concurrency)
            await new Promise((resolve) => queue.push(resolve));
        active++;
        try {
            return await fn();
        }
        finally {
            active--;
            queue.shift()?.();
        }
    };
};
const SCAN_CONCURRENCY = 4;
// One unserializable order 400s its whole page. Halving the failing page loses that order alone;
// both halves go out together, so a scan costs the depth of the halving, not its request count
// (13 broken orders among Sète's 527 took 73 sequential requests, 92 s).
const scanOrdersPage = async (client, filter, page, itemsPerPage, limit) => {
    try {
        const { orders } = await limit(() => fetchOrdersPage(client, filter, page, itemsPerPage));
        return { orders, unreadableOrders: 0 };
    }
    catch (err) {
        if (!isUnserializableOrderError(err))
            throw err;
        if (itemsPerPage === 1)
            return { orders: [], unreadableOrders: 1 };
        const half = itemsPerPage / 2;
        return combineScans(await Promise.all([
            scanOrdersPage(client, filter, 2 * page - 1, half, limit),
            scanOrdersPage(client, filter, 2 * page, half, limit),
        ]));
    }
};
const scanOrders = async (client, filter) => {
    const total = await countOrders(client, filter);
    const limit = createLimiter(SCAN_CONCURRENCY);
    const pages = Array.from({ length: Math.ceil(total / ORDERS_PER_PAGE) }, (_, i) => i + 1);
    return combineScans(await Promise.all(pages.map((page) => scanOrdersPage(client, filter, page, ORDERS_PER_PAGE, limit))));
};
// The billetterie sometimes returns 500 on `customer.id[]=X` for specific "cursed"
// customer IDs — even when X has orders, and even when alone. Other 0-order customers
// return a clean empty 200. We work around this by halving the chunk on 500 and treating
// a single-customer 500 as "no orders for that customer".
const fetchOrdersForChunk = async (client, filter, cursedCustomerIds) => {
    try {
        return await scanOrders(client, filter);
    }
    catch (err) {
        if (!(err instanceof TicketingApiError) || err.status !== 500)
            throw err;
        const { customerIds } = filter;
        if (customerIds.length === 1) {
            cursedCustomerIds.add(customerIds[0]);
            return { orders: [], unreadableOrders: 0 };
        }
        const mid = Math.floor(customerIds.length / 2);
        const scans = await Promise.all([
            fetchOrdersForChunk(client, { ...filter, customerIds: customerIds.slice(0, mid) }, cursedCustomerIds),
            fetchOrdersForChunk(client, { ...filter, customerIds: customerIds.slice(mid) }, cursedCustomerIds),
        ]);
        return combineScans(scans);
    }
};
const isOrderValid = (order) => order.state === 'fulfilled' && order.paymentState === 'paid';
const fetchOrderSubventionAdjustments = async (client, tokenValue) => {
    const response = await client.get(`/admin/orders/${encodeURIComponent(tokenValue)}/adjustments?type=subvention&itemsPerPage=100`);
    return z.array(orderAdjustmentSchema).parse(response['hydra:member'] ?? []);
};
class ProductTaxonResolver {
    constructor(client) {
        this.client = client;
        this.cache = new Map();
    }
    async getMainTaxonCode(productSlug) {
        if (this.cache.has(productSlug))
            return this.cache.get(productSlug) ?? null;
        try {
            const product = await this.client.get(`/admin/products/${productSlug}`);
            const parsed = productSchema.parse(product);
            const taxonIri = parsed.mainTaxon ?? null;
            const taxonCode = taxonIri ? (taxonIri.split('/').pop() ?? null) : null;
            this.cache.set(productSlug, taxonCode);
            return taxonCode;
        }
        catch {
            this.cache.set(productSlug, null);
            return null;
        }
    }
}
/**
 * Aggregate subvention amounts applied per billetterie category for one CSE
 * over a date range. Returns cents per taxon code.
 *
 * Costs (rough, preprod): O(cards) + O(orders) + O(valid_orders) HTTP requests.
 * The dominant cost is one /adjustments call per valid order, fanned out.
 */
export const getSubventionsByCategoryForCse = async (client, options) => {
    const { cseId, startDate, endDate } = options;
    const customerIds = await getCseCustomerIds(client, cseId);
    if (customerIds.length === 0) {
        return { totalsByTaxonCode: new Map(), unreadableOrders: 0, cursedCustomerIds: [] };
    }
    const cursedCustomerIds = new Set();
    const chunks = chunkArray(customerIds, CUSTOMER_CHUNK_SIZE);
    const { orders, unreadableOrders } = combineScans(await mapWithConcurrency(chunks, ORDER_CHUNK_CONCURRENCY, (customerIdsChunk) => fetchOrdersForChunk(client, { cseId: null, customerIds: customerIdsChunk, startDate, endDate }, cursedCustomerIds)));
    const validOrders = orders.filter(isOrderValid);
    // orderItem id → product slug, from the order payload we already have.
    const productSlugByItemId = new Map();
    for (const order of validOrders) {
        for (const item of order.items) {
            if (item.productSlug)
                productSlugByItemId.set(item.id, item.productSlug);
        }
    }
    const adjustmentLists = await mapWithConcurrency(validOrders, DEFAULT_CONCURRENCY, (order) => fetchOrderSubventionAdjustments(client, order.tokenValue));
    const resolver = new ProductTaxonResolver(client);
    const totals = new Map();
    for (const adjustments of adjustmentLists) {
        for (const adj of adjustments) {
            // Skip order-level subventions (e.g., "Subvention aux frais bancaires") — not category-tied.
            if (adj.type !== 'subvention' || adj.orderItem === null)
                continue;
            const itemId = extractIdFromIri(adj.orderItem);
            const slug = productSlugByItemId.get(itemId);
            if (!slug)
                continue;
            const taxonCode = await resolver.getMainTaxonCode(slug);
            if (!taxonCode)
                continue;
            // adj.amount is negative (it's a discount on the customer's side); the
            // subvention paid by the CSE is the absolute value.
            const cents = Math.abs(adj.amount);
            totals.set(taxonCode, (totals.get(taxonCode) ?? 0) + cents);
        }
    }
    return { totalsByTaxonCode: totals, unreadableOrders, cursedCustomerIds: [...cursedCustomerIds] };
};
// Wide bounds so "all history" is the default. The /admin/orders endpoint always filters on
// checkoutCompletedAt, which conveniently also drops never-completed carts (they have no date).
const ALL_TIME_START = '2000-01-01';
const ALL_TIME_END = '2100-01-01';
const MAX_ORDERS_PROBED = 200;
const toCustomerOrder = (order, subventionAmount) => ({
    id: order.id,
    number: order.number ?? null,
    checkoutCompletedAt: order.checkoutCompletedAt ?? null,
    state: order.state,
    paymentState: order.paymentState,
    total: order.total,
    subventionAmount,
    items: order.items.map((item) => ({
        productName: item.productName ?? null,
        variant: item.variant ?? null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.total,
    })),
    customerId: order.customer ? extractIdFromIri(order.customer) : null,
    paymentMethods: (order.payments ?? []).flatMap((payment) => payment.method ? [toPaymentMethod(payment.method)] : []),
    isDeferredPayment: (order.payments ?? []).some((payment) => payment.method && toPaymentMethod(payment.method).code === DEFERRED_PAYMENT_METHOD_CODE),
    customerPaymentMode: parseCustomerPaymentMode(order.notes ?? null),
    notes: order.notes ?? null,
});
// Each order carries the total CSE subvention applied to it (one /adjustments call per order,
// fanned out). A per-order adjustment failure degrades to 0 rather than failing the whole list.
const enrichWithSubvention = async (client, orders, concurrency) => {
    const subventions = await mapWithConcurrency(orders, concurrency, async (order) => {
        try {
            const adjustments = await fetchOrderSubventionAdjustments(client, order.tokenValue);
            return adjustments
                .filter((adjustment) => adjustment.type === 'subvention')
                .reduce((sum, adjustment) => sum + Math.abs(adjustment.amount), 0);
        }
        catch {
            return 0;
        }
    });
    return orders.map((order, index) => toCustomerOrder(order, subventions[index]));
};
const byCheckoutCompletedAtDesc = (a, b) => (b.checkoutCompletedAt ?? '').localeCompare(a.checkoutCompletedAt ?? '');
/**
 * Fetch one customer's billetterie order history (newest first), each order enriched with the CSE
 * subvention applied to it.
 *
 * The billetterie's /admin/orders 400s ("Unable to generate an IRI for Order") on any request whose
 * page contains an order it can't serialize. So: try one bulk request first (fast, works when nothing
 * is corrupt), and on that 400 fall back to fetching orders one at a time so a single un-serializable
 * order only drops itself. `failedCount` reports how many were lost.
 */
export const getOrdersForCustomer = async (client, customerId, options = {}) => {
    const { startDate = ALL_TIME_START, endDate = ALL_TIME_END, concurrency = DEFAULT_CONCURRENCY, maxOrders = MAX_ORDERS_PROBED, } = options;
    const filter = { cseId: null, customerIds: [customerId], startDate, endDate };
    const bulkPageSize = Math.max(1, Math.min(maxOrders, ORDERS_PER_PAGE));
    try {
        const { orders, total } = await fetchOrdersPage(client, filter, 1, bulkPageSize);
        if (orders.length >= total) {
            const enriched = await enrichWithSubvention(client, orders, concurrency);
            return {
                orders: enriched.sort(byCheckoutCompletedAtDesc),
                totalCount: total,
                failedCount: 0,
            };
        }
    }
    catch (err) {
        if (!(err instanceof TicketingApiError) || err.status !== 400)
            throw err;
    }
    // Degraded path: bulk failed (a corrupt order in the page) or there are more orders than one page.
    let total;
    try {
        total = await countOrders(client, filter);
    }
    catch {
        return { orders: [], totalCount: 0, failedCount: 0 };
    }
    const attempts = Math.min(total, maxOrders);
    const pages = Array.from({ length: attempts }, (_, i) => i + 1);
    const fetchedPerPage = await mapWithConcurrency(pages, concurrency, async (page) => {
        try {
            const { orders } = await fetchOrdersPage(client, filter, page, 1);
            return orders[0] ?? null;
        }
        catch (err) {
            if (err instanceof TicketingApiError && err.status === 400)
                return null;
            throw err;
        }
    });
    const fetched = fetchedPerPage.filter((order) => order !== null);
    const enriched = await enrichWithSubvention(client, fetched, concurrency);
    return {
        orders: enriched.sort(byCheckoutCompletedAtDesc),
        totalCount: total,
        failedCount: total - fetched.length,
    };
};
/**
 * The CSE's orders paid by « Paiement différé » between two dates (inclusive), whoever placed
 * them: the salarié, or a CSE agent from their own account.
 */
export const getCseDeferredOrders = async (client, cseId, { startDate, endDate }) => {
    const scan = await scanOrders(client, { cseId, customerIds: [], startDate, endDate });
    const deferred = scan.orders.filter((order) => (order.payments ?? []).some((payment) => payment.method && toPaymentMethod(payment.method).code === DEFERRED_PAYMENT_METHOD_CODE));
    const enriched = await enrichWithSubvention(client, deferred, DEFAULT_CONCURRENCY);
    return {
        orders: enriched.sort(byCheckoutCompletedAtDesc),
        unreadableOrders: scan.unreadableOrders,
    };
};
/**
 * One order of the CSE, read back from the billetterie rather than trusted from the browser. The
 * orders route filters on dates only, so the order is found through its exact checkout time.
 */
export const findCseOrder = async (client, cseId, orderId, checkoutCompletedAt) => {
    const filter = {
        cseId,
        customerIds: [],
        startDate: checkoutCompletedAt,
        endDate: checkoutCompletedAt,
    };
    const { orders } = await scanOrders(client, filter);
    const order = orders.find((candidate) => candidate.id === orderId);
    if (!order)
        return null;
    const [enriched] = await enrichWithSubvention(client, [order], 1);
    return enriched;
};
